<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { percentToDecimal } from '~/utils/number'

const user = useUser()
const { $request } = useNuxtApp()
const { data, isFetching } = useQuery({
  queryKey: ['/member/privileges'],
  queryFn: async ({ signal }) => {
    try {
      const res = await $request<{ data: Api.User.Privilege[] }>('/member/privileges', { signal })
      if (res.data?.length) {
        res.data = [...res.data, {
          privilegeId: 11,
          name: '体验版',
          type: 2,
          resource: 3,
          resourceName: '体验版',
          originalPrice: 0,
          realPrice: 0,
          discount: 0,
          privilegeValue: 1,
        }]
      }
      return res.data
    }
    catch (e) {
      return null
    }
  },
})

const payVal = ref(1)
const { t } = useI18n()
useSeoMeta({
  title: t('vip.pageTitle'),
  keywords: t('vip.pageKeyword'),
  description: t('vip.pageDescription'),
}, { tagPriority: 5 })
const payTypes = [
  {
    label: t('月付'),
    value: 1,
  },
  {
    label: t('年付'),
    value: 12,
  },
]
const vips: Record<string, any> = {
  // 6: {
  //   label: '子账号',
  //   dec: '仅适用于跨境新手体验',
  // },
  3: {
    label: '体验版',
    dec: '仅适用于跨境新手体验',
    list: [
      {
        label: '注册免费体验',
      },
      // {
      //   label: '支持部分筛选排序',
      //   limit: true,
      // },
      {
        label: '有限次数据搜索次数',
        limit: true,
      },
      {
        label: '有限次查看列表和榜单数据',
        limit: true,
      },
      {
        label: '有限次查看详情基础数据',
        limit: true,
      },
    ],
  },
  2: {
    label: '专业版',
    dec: '适用于本土/跨境商家、个人带货等',
    priviliage: {
      originPrice: '199',
      realPrice: '159',
      discount: '8',
    },
    list: [
      {
        label: '支持1个账号登录使用',
      },
      {
        label: '支持高级数据筛选排序',
      },
      {
        label: '查看更多列表和榜单数据',
        limit: true,
      },
      {
        label: '不限制搜索次数和查看详情',
      },
      {
        label: '支持列表/榜单/详情数据导出',
      },
      {
        label: '提供专属会员社群和VIP客服',
      },
      {
        label: '支持达人联系方式查看',
      },
    ],
  },
  1: {
    label: '企业版',
    dec: '适用于本土/跨境集团、品牌矩阵等',
    list: [
      {
        label: '支持1个账号+付费扩充子账号',
      },
      {
        label: '不限制查看列表和榜单数据',
      },
      {
        label: '不限制搜索次数和查看详情',
      },
      {
        label: '支持列表/榜单/详情数据导出',
      },
      {
        label: '提供专属会员社群和1V1客服',
      },
      {
        label: '支持达人联系方式查看和导出',
        limit: true,
      },
      {
        label: '优先享受最新功能升级体验',
        limit: true,
      },
    ],
  },
}
interface Privilege {
  resource: number
  resourceName: string
  discount: number
  privileges: Record<string, any>
  list: any[]
  privilegesList: any[]
}
const privileges = computed(() => {
  const groupedPrivileges = data.value?.reduce((acc: Record<string, Privilege>, privilege: Api.User.Privilege) => {
    const { resource, resourceName, privilegeValue } = privilege
    if (!acc[resource]) {
      acc[resource] = {
        resource,
        resourceName: resource === 6 ? '扩充团队' : resourceName,
        ...vips?.[resource],
        privileges: [],
      }
    }
    const month = Math.floor(privilegeValue / 30) // 按30天为一个月计算
    acc[resource].privileges.push({
      month,
      ...privilege,
      realPrice: privilege.realPrice ? percentToDecimal(`${privilege.realPrice}`) : privilege.realPrice,
      originalPrice: privilege.originalPrice ? percentToDecimal(`${privilege.originalPrice}`) : privilege.originalPrice,
      discount: privilege.discount ? privilege.discount / 10 : privilege.discount,
      dayAvgPrice: privilege.dayAvgPrice ? percentToDecimal(`${privilege.dayAvgPrice}`) : privilege.dayAvgPrice,
      promotionInfo: privilege.promotionInfo ? percentToDecimal(`${privilege.promotionInfo}`) : privilege.promotionInfo,
    })
    return acc
  }, {})
  return groupedPrivileges ? Object.values(groupedPrivileges) : []
})

// 把privileges按照resource倒序排列
const sortedPrivileges = computed(() => {
  return [...(privileges.value || [])].sort((a, b) => Number(b.resource) - Number(a.resource))
})
const { open } = useVipModal()
const route = useRoute()
function openDialog() {
  if (!privileges.value?.length)
    return
  // eslint-disable-next-line ts/no-unused-expressions
  route.query.type && open(1, Number(route.query.type))
}
watch(() => privileges.value, (val) => {
  user.value.privileges = val
  openDialog()
})
onMounted(() => {
  openDialog()
})
const filterPrivileges = computed(() => payVal.value === 1 ? sortedPrivileges.value?.filter(item => [1, 2, 3].includes(item.resource)) : sortedPrivileges.value?.filter(item => [2, 1].includes(item.resource)))
</script>

<template>
  <div class="min-w-1300">
    <a-spin w-full :loading="isFetching" :class="{ ' min-h-1200 bg-white': isFetching && !privileges.length }">
      <div class="g-vip__header h-96 rd-8 text-center text-48 color-white font-200 lh-96">
        {{ $t('重磅升级，会员钜惠，限时抢购') }}!
      </div>
      <g-card v-if="privileges.length" class="g-vip__content relative mt-20 w-full pb-40 !bg-#D2DAF8">
        <div class="g-vip__info flex flex-col items-center gap-48">
          <div text-white font-300>
            <div text-70>
              {{ $t('会员权益') }}
            </div>
            <div mt-8 text-18 font-300 tracking-4.4>
              MEMBERSHIP BENEFITS
            </div>
          </div>
          <div class="h-60 w-fit flex rd-100 bg-[rgba(255,255,255,0.5)]">
            <div
              v-for="item in payTypes" :key="item.value"
              class="w-167 cursor-pointer rd-30 text-center text-[--color-text-1] font-500 lh-60"
              :class="{ 'bg-white': item.value === payVal }"
              @click="payVal = item.value"
            >
              {{ item.label }}
            </div>
          </div>
          <div min-w-1170 flex justify-between gap-24>
            <UserVipCard v-for="item in filterPrivileges" :key="item.resource" :pay-type="payVal" :data="item" :class="{ '!w-400': filterPrivileges.length > 2 }" />
          </div>
        </div>
        <!-- 会员权益对比 -->
        <UserVipPrivilege :data="privileges.slice(0, 3)" />
        <!-- Q & A -->
        <UserVipQuestion />
      </g-card>
    </a-spin>
  </div>
</template>

<style lang="less" scoped>
.g-vip {
    &__header {
      width: 100%;
      background-image: url('~/assets/images/user/vip/vip-tab.png');
      background-size: cover;
      background-repeat: no-repeat;
    }
    &__content {
      background:
      url('~/assets/images/user/vip/vip-bg1.png') center top/100% auto no-repeat,
      // url('~/assets/images/user/vip-bg2.png') center 960px/100% auto no-repeat,

    }
}
</style>
